// V2 版式：matrix（前端 DOM 版）——对照后端 _shared/pptx/v2/layouts/matrix.ts 逐坐标移植
// 6–9 格矩阵小卡，3 列网格等距，供料 cards block；有 metric block 且卡数 ≥6 时左上 1×1 格放 hero 数字。
// 瑞士：grey1 底直角格；杂志：格间 hairline 分隔无底色。

import { SPACING } from "../../lib/tokens.generated";
import { V2GEO } from "../../lib/v2geo.generated";
import { px, pt, titleSize, kickerOf, footOf } from "./shared";
import { luxeExt } from "./luxe";
import {
  consultTheme, brutalTheme, termTheme, reportTheme, inkTheme, memphisTheme, memphisPalette,
} from "./themes";
import V2Chrome from "./Chrome";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

export default function V2Matrix({ c, skin, page, total, editable, handlers }: V2LayoutProps) {
  // 与后端 matrix.ts GEO 同源（deno dump 生成，见 v2geo.generated.ts 头部注释）
  const GEO = V2GEO["matrix"] as any;
  const m = SPACING.marginX;
  const W = 13.333;
  const mag = skin.style === "magazine";
  // 格底色：瑞士取主题 grey1（后端 matrix.ts:39；杂志无底色，不消费此值）
  const theme = skin.theme as { grey?: { grey1?: string } };
  const grey1 = theme.grey?.grey1 ?? skin.hairline;
  // luxe 黑格配色（matrix.ts 分支）：ink 底格 + 米白标题 + 灰一描述；hero 数字格保持裸金数字
  const ext = luxeExt(skin);
  // 主题库扩展 6 风格（044 T3.3）：格底/边框/文字用色分支（matrix.ts:86-147 逐风格移植）
  const consultT = consultTheme(skin);
  const brutalT = brutalTheme(skin);
  const termT = termTheme(skin);
  const reportT = reportTheme(skin);
  const inkT = inkTheme(skin);
  const mpT = memphisTheme(skin);

  const bi = c.blocks.findIndex((x) => x.type === "cards");
  const b = bi >= 0 ? c.blocks[bi] : null;
  const mi = c.blocks.findIndex((x) => x.type === "metric");
  const mb = mi >= 0 ? c.blocks[mi] : null;
  // hero 数字格：有 metric 且卡片数 ≥6 时启用（占左上 1×1）
  const hero = mb && mb.type === "metric" && b && b.type === "cards" && b.items.length >= 6 ? mb : null;
  const items = (b && b.type === "cards" ? b.items : []).slice(0, hero ? 8 : 9);

  const n = items.length + (hero ? 1 : 0);
  const rows = n <= 3 ? 1 : n <= 6 ? 2 : 3;
  const cols = GEO.cols as number;

  const gridW = W - 2 * m;
  const gridH = GEO.gridBottom - GEO.gridY;
  const cw = n > 0 ? (gridW - (cols - 1) * GEO.gap) / cols : 0;
  const ch = n > 0 ? (gridH - (rows - 1) * GEO.gap) / rows : 0;
  const cellX = (idx: number) => m + (idx % cols) * (cw + GEO.gap);
  const cellY = (idx: number) => GEO.gridY + Math.floor(idx / cols) * (ch + GEO.gap);

  return (
    <>
      <V2Chrome skin={skin} kicker={kickerOf(page, "matrix")} page={page} total={total} foot={footOf()} />

      {/* 页标题 */}
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(m), top: px(GEO.titleY), width: px(W - 2 * m), height: px(GEO.titleH),
          fontSize: pt(titleSize(c.title, skin.scale.pageTitle)), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: mag ? 700 : 400, lineHeight: 1.1,
        }}
      />
      {/* 副标题 */}
      {(c.subtitle || editable) && (
        <EditableText
          value={c.subtitle ?? ""} path="subtitle" editable={editable}
          onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
          style={{
            position: "absolute", left: px(m), top: px(GEO.subY), width: px(W - 2 * m - 2), height: px(GEO.subH),
            fontSize: pt(skin.scale.body), color: skin.sub, fontFamily: skin.fontBody,
          }}
        />
      )}

      {n > 0 && (
        <>
          {/* 格子底（matrix.ts:86-129 分支）；consult 微圆角灰格 / brutal 白底 3px 粗黑框 /
              term 无填充细框面板 / report inkDeep 暗格 / ink 无底色（分隔线单独画）/
              memphis 白底大圆角格+彩头带 / 瑞士灰格（luxe ink 黑格）/ 杂志无底色 */}
          {Array.from({ length: n }, (_, i) => {
            const x = cellX(i);
            const y = cellY(i);
            if (inkT) return null;
            const headerColor = mpT ? memphisPalette(mpT)[i % 5] : "";
            const bg = consultT ? grey1
              : brutalT ? brutalT.paper
                : termT ? "transparent"
                  : reportT ? reportT.inkDeep
                    : mpT ? "#FFFFFF"
                      : mag ? "transparent"
                        : ext ? ext.ink : grey1;
            const border = brutalT ? `3px solid ${brutalT.ink}`
              : termT ? `1px solid ${termT.muted}`
                : reportT ? `1px solid ${reportT.muted}`
                  : "none";
            const radius = (consultT || mpT) ? px(skin.radius) : undefined;
            return (
              <div key={`bg${i}`}>
                {bg !== "transparent" && (
                  <div style={{
                    position: "absolute", left: px(x), top: px(y), width: px(cw), height: px(ch),
                    background: bg, borderRadius: radius, border,
                  }} />
                )}
                {mpT && (
                  <>
                    <div style={{
                      position: "absolute", left: px(x), top: px(y), width: px(cw), height: px(0.4),
                      background: headerColor, borderRadius: `${px(skin.radius)} ${px(skin.radius)} 0 0`,
                    }} />
                    <div style={{
                      position: "absolute", left: px(x), top: px(y + 0.24), width: px(cw), height: px(0.16),
                      background: headerColor,
                    }} />
                  </>
                )}
              </div>
            );
          })}
          {/* 杂志/ink：列间 / 行间中线分隔（ink 用黛灰细发线） */}
          {mag && Array.from({ length: cols - 1 }, (_, k) => k + 1).map((col) => (
            <div key={`v${col}`} style={{
              position: "absolute", left: px(m + col * cw + (col - 0.5) * GEO.gap), top: px(GEO.gridY),
              width: 1, height: px(gridH), background: skin.hairline,
            }} />
          ))}
          {mag && Array.from({ length: rows - 1 }, (_, k) => k + 1).map((r) => (
            <div key={`h${r}`} style={{
              position: "absolute", left: px(m), top: px(GEO.gridY + r * ch + (r - 0.5) * GEO.gap),
              width: px(gridW), height: 1, background: skin.hairline,
            }} />
          ))}
          {inkT && Array.from({ length: cols - 1 }, (_, k) => k + 1).map((col) => (
            <div key={`iv${col}`} style={{
              position: "absolute", left: px(m + col * cw + (col - 0.5) * GEO.gap), top: px(GEO.gridY),
              width: 1, height: px(gridH), background: inkT.accentGrey,
            }} />
          ))}
          {inkT && Array.from({ length: rows - 1 }, (_, k) => k + 1).map((r) => (
            <div key={`ih${r}`} style={{
              position: "absolute", left: px(m), top: px(GEO.gridY + r * ch + (r - 0.5) * GEO.gap),
              width: px(gridW), height: 1, background: inkT.accentGrey,
            }} />
          ))}

          {/* hero 数字格（左上 1×1） */}
          {hero && (
            <>
              <EditableText
                value={hero.value} path={`blocks.${mi}.value`} editable={editable}
                onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
                style={{
                  position: "absolute", left: px(cellX(0) + GEO.padX), top: px(cellY(0) + GEO.padTop),
                  width: px(cw - 2 * GEO.padX), height: px(ch * 0.55),
                  fontSize: pt(Math.round(skin.scale.dataHero * 0.55)), color: skin.accent,
                  fontFamily: skin.fontHero, lineHeight: 1, display: "flex", alignItems: "center",
                }}
              />
              <EditableText
                value={hero.label} path={`blocks.${mi}.label`} editable={editable}
                onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
                style={{
                  position: "absolute", left: px(cellX(0) + GEO.padX), top: px(cellY(0) + ch - 0.55),
                  width: px(cw - 2 * GEO.padX), height: px(0.4),
                  fontSize: pt(skin.scale.caption), color: ext ? ext.grey1 : skin.sub, fontFamily: skin.fontBody,
                }}
              />
            </>
          )}

          {/* 内容格 */}
          {items.map((it, i) => {
            const idx = (hero ? 1 : 0) + i;
            const x = cellX(idx);
            const y = cellY(idx);
            // consult 语义徽章（043 §4.2）：格标题上方短色条，语义色轮用；memphis 标题进彩头带
            const sem = ["accentEmerald", "accentGold", "accentCoral", "accentSlate"] as const;
            const semColor = consultT
              ? (consultT as unknown as Record<string, string>)[sem[idx % 4]] ?? skin.accent
              : "";
            const ctY = consultT ? GEO.padTop + 0.12 : GEO.padTop;
            const titleColor = mpT
              ? (idx % 5 === 4 ? mpT.ink : "#FFFFFF")
              : ext ? ext.paper : skin.text;
            return (
              <div key={`c${i}`}>
                {consultT && (
                  <div style={{
                    position: "absolute", left: px(x + GEO.padX), top: px(y + GEO.padTop),
                    width: px(0.3), height: px(0.04), background: semColor,
                  }} />
                )}
                <EditableText
                  value={it.title} path={`blocks.${bi}.items.${i}.title`} editable={editable} multiline
                  onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
                  style={{
                    position: "absolute", left: px(x + GEO.padX),
                    top: px(mpT ? y + 0.05 : y + ctY),
                    width: px(cw - 2 * GEO.padX), height: px(mpT ? 0.32 : GEO.ctH),
                    fontSize: pt(skin.scale.body), color: titleColor, fontFamily: skin.fontBody,
                    fontWeight: 700, lineHeight: 1.1,
                  }}
                />
                {(it.desc || editable) && (
                  <EditableText
                    value={it.desc ?? ""} path={`blocks.${bi}.items.${i}.desc`} editable={editable} multiline
                    onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
                    style={{
                      position: "absolute", left: px(x + GEO.padX), top: px(y + GEO.ddY),
                      width: px(cw - 2 * GEO.padX), height: px(ch - GEO.ddY - GEO.ddPad),
                      fontSize: pt(skin.scale.caption), color: ext ? ext.grey1 : skin.sub, fontFamily: skin.fontBody, lineHeight: 1.3,
                    }}
                  />
                )}
              </div>
            );
          })}
        </>
      )}
    </>
  );
}
